import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { FilterChangeEvent } from '@progress/kendo-react-data-tools'
import { NameDto } from 'api/Common/Types'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { IntroducingBrokerDetailsDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import {
  FormBaseButtonActionWrapper,
  FormBaseContainer,
  FormBaseNavigationHeader,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { normalizeFilter, resetOperatorOnFieldChange } from 'features/DataGrid'
import { AuditLogIcon } from 'icons/AuditLogIcon'
import { ChevronDown } from 'lucide-react'
import { useAppDispatch } from 'store'
import {
  fetchIBRebatesApprovalTypes,
  fetchIBRebatesPeriods,
} from 'store/partnerships/partnerships.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { hasPermission } from 'utils/permissionsValidator'

import ClientAreaAuditLogModal from '../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { getIBRebatesGroupFilters } from '../../../../components/Filter/getIBRebatesGroupFilters'
import { getIBRebatesTAFilters } from '../../../../components/Filter/getIBRebatesTAFilters'
import {
  IntroducingBrokerNavigation,
  IntroducingBrokerTab,
} from '../Navigation/IntroducingBrokerNavigation'
import { IBRebatesGroupSettings } from './IBRebatesGroupSettings'
import { IBRebatesTASettings } from './IBRebatesTASettings'

interface Props {
  data: IntroducingBrokerDetailsDto
}

const IntroducingBrokerRebatesPage: React.FC<Props> = (props) => {
  const { data } = props
  const [auth] = useContext(AuthSessionContext)
  const [groups, setGroups] = useState<NameDto<string>[]>([])
  const dispatch = useAppDispatch()
  const ibRebateGroupsRef = useRef<{ refreshGroups: () => void; resetSelection: () => void }>()
  const ibRebateTASettingsRef = useRef<{ refreshRebates: () => void; resetSelection: () => void }>()

  const permissions = auth?.permissions || []
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [isEdit, setEdit] = useState(false)
  const [auditLogVisible, setAuditLogVisible] = useState(false)

  const [taFilter, setTaFilter] = useState<CompositeFilterDescriptor>({ logic: 'or', filters: [] })
  const [groupFilter, setGroupFilter] = useState<CompositeFilterDescriptor>({
    logic: 'or',
    filters: [],
  })

  const onTaFilterChange = (event: FilterChangeEvent) => {
    const normalized = normalizeFilter(event.filter)
    setTaFilter((prev) => resetOperatorOnFieldChange(prev, normalized))
  }

  const onGroupFilterChange = (event: FilterChangeEvent) => {
    const normalized = normalizeFilter(event.filter)
    setGroupFilter((prev) => resetOperatorOnFieldChange(prev, normalized))
  }

  useEffect(() => {
    dispatch(fetchIBRebatesApprovalTypes(partnershipsApiClient))
    dispatch(fetchIBRebatesPeriods(partnershipsApiClient))
  }, [])

  return (
    <React.Fragment>
      <IntroducingBrokerNavigation data={data} isEdit={false} tab={IntroducingBrokerTab.REBATES} />
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          introducingBrokerRebatesId={data.id}
        />
      )}

      <FormBaseButtonActionWrapper>
        <FormBaseNavigationHeader>
          <ButtonGroup>
            {!isEdit ? (
              <Button
                disabled={
                  !hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerRebate)
                }
                onClick={() => setEdit(true)}
                data-test='edit-button'
              >
                Edit
              </Button>
            ) : (
              <React.Fragment>
                <Button
                  onClick={() => {
                    ibRebateTASettingsRef.current?.resetSelection()
                    ibRebateGroupsRef.current?.resetSelection()
                    setEdit(false)
                  }}
                  data-test='cancel-button'
                >
                  Done
                </Button>
              </React.Fragment>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    aria-label='More options'
                    data-test='more-button'
                    className='more-button'
                  />
                }
              >
                <ChevronDown />
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onSelect={() => setAuditLogVisible(true)}
                  data-test='audit-log-menu-button'
                >
                  <AuditLogIcon style={{ width: 12, height: 12 }} /> Audit Log
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        </FormBaseNavigationHeader>
      </FormBaseButtonActionWrapper>

      <FormBaseContainer>
        <FormBaseRow>
          <IBRebatesTASettings
            ref={ibRebateTASettingsRef}
            groups={groups}
            refreshGroups={ibRebateGroupsRef.current?.refreshGroups}
            data={data}
            isEdit={isEdit}
            taFilter={taFilter}
            onTaFilterChange={onTaFilterChange}
            taFilterFields={getIBRebatesTAFilters()}
          />
          <IBRebatesGroupSettings
            ref={ibRebateGroupsRef}
            refreshRebates={ibRebateTASettingsRef.current?.refreshRebates}
            setGroups={setGroups}
            data={data}
            isEdit={isEdit}
            groupFilter={groupFilter}
            onGroupFilterChange={onGroupFilterChange}
            groupFilterFields={getIBRebatesGroupFilters()}
          />
        </FormBaseRow>
      </FormBaseContainer>
    </React.Fragment>
  )
}

export default IntroducingBrokerRebatesPage
